import React from "react"
import DatePicker, { registerLocale } from "react-datepicker"
import { useId } from "@reach/auto-id"

import "react-datepicker/dist/react-datepicker.css"
import { enGB, fr, es, de } from "date-fns/locale"
import dateService from "services/date"
import { isEnglishLanguage, isGermanLanguage, isSpanishLanguage, isFrenchLanguage } from "utils/i18n"
import { CalendarIcon } from "components/Elements/Icons"

import "./InputDate.scoped.scss"

export const InputDate = ({
    id,
    name,
    min,
    max,
    value,
    label,
    onChange,
    disabled,
    required,
    type,
    style,
    className,
    classNameLabel = "",
    classNameInput = "",
}) => {
    const [errors] = React.useState([])
    const [selectedDate, setSelectedDate] = React.useState(new Date(value) || new Date())
    const [locale, setLocale] = React.useState("fr")
    const pickerRef = React.useRef(null)

    const prefix = useId()
    const inputId = id ?? `${prefix}-${name}`
    const errorId = `${inputId}-error`

    registerLocale("fr", fr)
    registerLocale("es", es)
    registerLocale("de", de)
    registerLocale("enGB", enGB)

    const handleChange = (value) => {
        const inputValue = {
            target: {
                type: "date",
                name: name,
                value: dateService.formatDate(value, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx")
            }
        }
        onChange(inputValue) 
        setSelectedDate(value)
    }

    React.useEffect(() => {
        if(isFrenchLanguage()) setLocale("fr")
        if(isEnglishLanguage()) setLocale("enGB")
        if(isGermanLanguage()) setLocale("de")
        if(isSpanishLanguage()) setLocale("es")
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <>
            <div data-cy={`formfield-${name}`} className={"c-form-group " + (className)} style={style}>
                <label data-cy={`label-${name}`} className={"c-label mb-1 " + classNameLabel} htmlFor={inputId}>{label} {required && "*"}</label>

                <div data-cy={`input-${name}`} className="c-form-group__form">
                    <DatePicker
                        ref={pickerRef}
                        id={inputId}
                        selected={selectedDate}
                        disabled={disabled}
                        required={required}
                        locale={locale}
                        showYearDropdown
                        minDate={min ? new Date(min) : new Date("1970-01-01Z00:00:00:000")}
                        maxDate={new Date(max)}
                        scrollableYearDropdown
                        closeOnScroll={true}
                        dateFormat={dateService.getCurrentShortDateFormat() ?? "dd/MM/y"}
                        className={"c-datepicker " + (classNameInput ? classNameInput : "")}
                        onChange={(value) => !disabled && handleChange(value)}
                        // Prevent keyboard opening on mobile
                        // https://github.com/Hacker0x01/react-datepicker/issues/1640
                        onFocus={(e) => e.target.readOnly = true}
                        aria-describedby={errorId}
                        aria-invalid={Boolean(errors.length)}
                    />
                    <div data-cy={`button-${name}`} className="c-icon c-icon--end" aria-hidden="true" onClick={() => pickerRef.current.setOpen(true)}>
                        <CalendarIcon />
                    </div>
                </div>
            </div>
        </>
    )
}
